/**
 * Mythium Background Removal
 *
 * Wraps @imgly/background-removal as an ES module dynamic import (CDN-loaded).
 * Module is loaded lazy: only when user clicks "Remove background" the first time.
 * Subsequent calls reuse the cached module.
 */

(function () {
  'use strict';

  const CDN_URL = 'https://cdn.jsdelivr.net/npm/@imgly/background-removal@1.6.0/+esm';
  let modulePromise = null;
  let activeController = null;

  function loadModule() {
    if (!modulePromise) {
      modulePromise = import(CDN_URL).catch((err) => {
        modulePromise = null;
        throw err;
      });
    }
    return modulePromise;
  }

  /**
   * Remove background from a File. Returns a new File (PNG with transparency).
   * Aborts any previous in-flight removal automatically.
   */
  async function removeBgFromFile(file, onProgress) {
    abortActive();
    const controller = new AbortController();
    activeController = controller;

    // Yield to browser so spinner can paint before WASM starts
    await new Promise((resolve) => setTimeout(resolve, 80));

    try {
      const mod = await loadModule();
      if (controller.signal.aborted) return null;

      const blob = await mod.removeBackground(file, {
        output: { format: 'image/png', quality: 1 },
        proxyToWorker: true,
        progress: typeof onProgress === 'function' ? onProgress : undefined,
      });

      if (controller.signal.aborted) return null;

      const baseName = file.name.replace(/\.[^.]+$/, '');
      return new File([blob], baseName + '-nobg.png', { type: 'image/png' });
    } catch (err) {
      if (controller.signal.aborted) return null;
      throw err;
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function abortActive() {
    if (activeController) {
      activeController.abort();
      activeController = null;
    }
  }

  // Expose on a global so mythium-configurator.js can use it
  window.MythiumBgRemoval = {
    removeBgFromFile: removeBgFromFile,
    abortActive: abortActive,
  };
})();
